import { PureComponent, ReactNode, CSSProperties, MouseEvent } from 'react';
import _ from "lodash";
import Tippy from "@tippyjs/react";
import "./Button.less";
import { getZDepthShadow } from "../../Utils/Colors";
import { IsNullOrWhiteSpace, IsNullOrEmpty, isDefined, isFunction } from "../../Utils/Micro";
import { getIconClassName } from "./IconSelectorUtils";
import { LabelAutoSize } from "./Typography";
import { IAdvancedTippy, AdvancedTippy } from '@Primitive/AdvancedTippy';
import { Props } from '@Utils/ReactProps';
// ================================================================================================
export const enum ButtonLabelPosition {
    bottom = 0,
    bottom_str = "bottom",
    top = 1,
    top_str = "top",
    left = 2,
    left_str = "left",
    right = 3,
    right_str = "right"
}
// ------------------------------------------------------------------------------------------------
export enum ButtonValueType {
    Text = 0,
    FontIcon = 1,
    Image = 2
}
// ------------------------------------------------------------------------------------------------
interface ButtonProps extends Props<Button> {
    width?: string | number;
    height?: string | number;

    /**
     * Button tooltip
     */
    tooltip?: string;

    /**
     * Button icon
     */
    icon?: string;

    /**
     * Icon type.
     * Default: ButtonValueType.FontIcon
     */
    iconType?: ButtonValueType;

    /**
     * Button icon color, Valid if icon is className string
     */
    iconFontColor?: string;

    /**
     * Button font icon, Valid if icon is className string
     */
    iconFontSize?: string | number;
    iconWidth?: number;

    /**
     * Button Label
     */
    label: string;

    /**
     * Label position.
     * Default: LabelPosition.right
     */
    labelPosition?: ButtonLabelPosition;

    /**
     * Auto font size.
     *
     */
    labelAutoSize?: boolean;

    /**
     * Max font size when we are using auto sizing
     */
    labelMaxFontSize?: number;

    /**
     * Shadow level
     * Default: 1
     */
    shadowLevel?: number;

    /**
     * Border radius
     */
    borderRadius?: string | number;

    /**
     * Button css style overrides
     */
    style?: CSSProperties;

    /**
     * Button label css style overrides
     */
    styleLabel?: CSSProperties;

    /**
     * OnClick event
     */
    onClick: () => void;

    /**
     * Tab index that we pass to button
     */
    tabIndex?: number;

    /**
     * Disabled button
     */
    disabled?: boolean;

    /**
     * Enable showing bubble
     */
    showBubble?: boolean;

    /**
     * Configure bubble content
     */
    bubbleContent?: string;
    /**
     * Configure bubble background color
     */
    bubbleBackgroundColor?: string;

    /**
     * Configure bubble text color
     */
    bubbleColor?: string;

    /**
   * Configure bubble size
   */
    bubbleSize?: string | number;

    className?: string;

    //--------------------------------------------------
    splitButton?: boolean;
    advancedTooltip?: IAdvancedTippy;
    advancedPopup?: IAdvancedTippy;

    buttonPressed?: (element: any, data: any) => void;
    elementChanged?: (dataPath: string, data: any, prevData: any, formData: any) => void;
    //--------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
export class Button extends PureComponent<ButtonProps> {
    constructor(props: ButtonProps) {
        super(props);
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let style: CSSProperties = {
            width: this.props.width ?? 'auto',
            height: this.props.height ?? 'auto',
            boxShadow: getZDepthShadow(this.props.shadowLevel ?? 1),
            borderRadius: this.props.borderRadius ?? 4,
            ...this.props.style
        };

        let icon = null;
        let isOnlyImage = false;
        let isImageCentered = false;

        // necessary styling changes according to position
        const labelPosition = this.props.labelPosition ?? ButtonLabelPosition.right;
        switch (labelPosition) {
            case ButtonLabelPosition.top_str:
            case ButtonLabelPosition.top:
                style.flexDirection = 'column-reverse';
                isImageCentered = true;
                break;
            case ButtonLabelPosition.bottom_str:
            case ButtonLabelPosition.bottom:
                style.flexDirection = 'column';
                isImageCentered = true;
                break;
            case ButtonLabelPosition.left_str:
            case ButtonLabelPosition.left:
                style.flexDirection = 'row-reverse';
                break;
            case ButtonLabelPosition.right_str:
            case ButtonLabelPosition.right:
                style.flexDirection = 'row';
                break;
            default:
                break;
        }

        const hasLabel = !IsNullOrWhiteSpace(this.props.label);
        if (!IsNullOrWhiteSpace(this.props.icon)) {
            switch (this.props.iconType) {
                case ButtonValueType.FontIcon:
                    icon = (
                        <i className={getIconClassName(this.props.icon)}
                            style={{
                                color: this.props.iconFontColor,
                                fontSize: this.props.iconFontSize,
                                width: this.props.iconWidth,
                                flex: "unset",
                                padding: 10
                            }} />
                    );
                    break;
                case ButtonValueType.Image:
                    isOnlyImage = !hasLabel;
                    icon = (
                        <img
                            src={this.props.icon}
                            style={{
                                flex: "unset",
                                padding: 10,
                                maxWidth: this.props.iconWidth ?? null,
                                maxHeight: this.props.iconWidth ?? null
                            }} />
                    );
                    break;
                default:
                    break;
            }
        }

        let label: string | ReactNode = null;
        if (hasLabel) {
            if (this.props.labelAutoSize ?? false) {
                label = (
                    <LabelAutoSize
                        Height={undefined}
                        Width={undefined}
                        Text={this.props.label}
                        MaxFontSize={this.props.labelMaxFontSize}
                        Style={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        }} />
                );
            }
            else {
                label = this.props.label;
            }
        }

        if (this.props.disabled) {
            style.backgroundColor = "#CCC";
            style.opacity = 0.5;
            style.pointerEvents = "none";
        }

        const isAdvanced = isDefined(this.props.advancedTooltip) || isDefined(this.props.advancedPopup);

        // no hover if it's an image
        let className = 'yodiwo-button';
        if (isOnlyImage || (isAdvanced && !this.props.splitButton)) {
            className += ' no-hover';
        }

        if (!IsNullOrEmpty(this.props.className)) {
            className += ' ' + this.props.className;
        }

        // button creation
        let button = (
            <div
                style={style}
                className={className}
                onClick={this.props.disabled ? _.noop : this.props.onClick}
                tabIndex={this.props.tabIndex}>
                {this.props.showBubble && !IsNullOrEmpty(this.props.bubbleContent)
                    ? <div style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: this.props.bubbleBackgroundColor,
                        color: this.props.bubbleColor,
                        fontSize: 10,
                        borderRadius: '100%',
                        height: this.props.bubbleSize,
                        width: this.props.bubbleSize
                    }}>
                        <span style={{
                            display: 'inline-block',
                            width: this.props.bubbleSize,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {this.props.bubbleContent}
                        </span>
                    </div>
                    : null}
                {icon}
                {label == null && isImageCentered ? null :
                    <div style={{
                        flex: (isImageCentered) ? undefined : 1,
                        ...(this.props.styleLabel ?? {})
                    }}>
                        {label}
                    </div>}
                {/* add caret? */}
                {isAdvanced && this.props.splitButton
                    ? <div onClick={event => event.stopPropagation()}>
                        <AdvancedTippy
                            placement='bottom'
                            tooltip={null}
                            advancedTooltip={this.props.advancedTooltip}
                            advancedPopup={this.props.advancedPopup}
                            iconClicked={null}
                            buttonPressed={this.props.buttonPressed}
                            elementChanged={this.props.elementChanged}>
                            <button
                                className='split-button'
                                style={{
                                    zIndex: 1,
                                    height: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    padding: '0 12px',
                                    backgroundColor: style.backgroundColor,
                                    borderRadius: style.borderRadius
                                }}>
                                <i className='fas fa-angle-down' />
                            </button>
                        </AdvancedTippy>
                    </div>
                    : null}
            </div>
        );

        if (isAdvanced && !this.props.splitButton) {
            // wrap the whole button
            return (
                <AdvancedTippy
                    placement='bottom'
                    tooltip={this.props.tooltip}
                    advancedTooltip={this.props.advancedTooltip}
                    advancedPopup={this.props.advancedPopup}
                    iconClicked={null}
                    buttonPressed={this.props.buttonPressed}
                    elementChanged={this.props.elementChanged}>
                    {button}
                </AdvancedTippy>
            );
        } else if (!IsNullOrWhiteSpace(this.props.tooltip)) {
            return (
                <Tippy
                    interactive
                    hideOnClick
                    animation={false}
                    appendTo={document.body}
                    trigger='mouseenter touchenter'
                    onTrigger={(instance, event) => {
                        // reset the opacity which was zero to solve artifacts
                        instance.popper.style.opacity = '1';
                    }}
                    onUntrigger={(instance, event) => {
                        // set a zero opacity to solve artifacts
                        instance.popper.style.opacity = '0';
                        instance.hide();
                    }}
                    onShow={() => {
                        // is it an ios device?
                        if (/iPhone|iPad|iPod/.test(navigator.platform) &&
                            !this.props.disabled &&
                            isFunction(this.props.onClick))
                            this.props.onClick();
                    }}
                    placement='bottom'
                    content={this.props.tooltip}>
                    {button}
                </Tippy>
            );
        } else {
            return button;
        }
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
interface IconButtonProps {
    key?: string;
    iconClassName: string;
    onClick: (event: MouseEvent) => void;
    tooltip?: string;
    iconStyle?: CSSProperties;
    disabled?: boolean;
}
// ------------------------------------------------------------------------------------------------
export function IconButton(props: IconButtonProps) {
    let iconClassName = props.iconClassName ?? "";

    let icon = <div
        className={`yodiwo-icon-button ${iconClassName}`}
        onClick={!props.disabled && isFunction(props.onClick) ? event => props.onClick(event) : undefined}
        style={{
            opacity: props.disabled ? 0.5 : 1,
            cursor: props.disabled ? 'not-allowed' : 'pointer',
            ...props.iconStyle
        }}
    />;

    // Use tippy for tooltip
    if (!IsNullOrWhiteSpace(props.tooltip))
        return <Tippy
            animation={false}
            appendTo={document.body}
            placement="bottom"
            content={props.tooltip}
        >
            {icon}
        </Tippy>

    return icon;
}

// ------------------------------------------------------------------------------------------------